import Section from "../Section";
import { useEffect, useMemo, useState } from "react";
import { Card, ImprovementGoal, Recommendation as RecommendationType, Survey } from "../../types";
import { useApiData } from "../../contexts/ApiData";
import CheckSubscription from "../../CheckSubscription";
import Recommendation from "./Recommendation";
import { useSearchParams } from "react-router-dom";
import { InfoMessage } from "../InfoMessage/InfoMessage";
import { useTranslations } from "../../contexts/Translation";
import { H2 } from "../Headers/Headings";
import { SuccessIcon } from "../Icons";
import LatestCardHero from "./LatestCardHero";
import YearSummary from "./YearSummary";
import SurveyList from "./SurveyList";
import GoalList from "./GoalList";
import { alerts, CategoryScores, yearStats } from "./insights";
import styles from "./Index.module.css";

const Dashboard = () => {
  const { get } = useApiData();
  const [card, setCard] = useState<Card | null>(null);
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [goals, setGoals] = useState<ImprovementGoal[]>([]);
  const [recommendations, setRecommendations] = useState<RecommendationType[] | undefined>(undefined);
  const [searchParams] = useSearchParams();
  const t = useTranslations("dashboard");
  const showWelcome = searchParams.get("welcome") === "true";

  useEffect(() => {
    const call = async () => {
      const [card, surveyList, goalList, recs] = await Promise.all([
        get(`/card?latest=true`),
        get(`/survey`),
        get(`/goals`),
        get(`/recommendations`),
      ]);
      if (card) {
        setCard(card);
      }
      setSurveys(surveyList || []);
      setGoals(goalList || []);
      setRecommendations(recs || []);
    };
    call();
  }, [get]);

  // Category scores come with /survey, one average per question.
  const scoresById = useMemo<Record<string, CategoryScores>>(
    () => Object.fromEntries(surveys.map((s) => [s.id, s.categoryScores ?? {}])),
    [surveys],
  );
  const stats = useMemo(() => yearStats(surveys, goals), [surveys, goals]);
  const alertList = useMemo(() => alerts(surveys, scoresById, recommendations || []), [surveys, scoresById, recommendations]);

  return (
    <>
      {showWelcome && (
        <Section>
          <InfoMessage type='success'>
            <div className={styles.welcomeContent}>
              <SuccessIcon width='24px' height='24px' />
              <div className={styles.welcomeText}>
                <H2 variant='compact'>{t("welcomeTitle")}</H2>
                <div>{t("welcomeMessage")}</div>
              </div>
            </div>
          </InfoMessage>
        </Section>
      )}
      <CheckSubscription />
      <div className={styles.mainColumn}>
        <Section>
          <SurveyList surveys={surveys} scoresById={scoresById} />
        </Section>
        <Section>
          <GoalList goals={goals} surveys={surveys} />
        </Section>
        <Section>
          <LatestCardHero card={card} />
        </Section>
      </div>
      <div className={styles.sideColumn}>
        {recommendations?.length ? (
          <Section>
            <Recommendation recommendations={recommendations} />
          </Section>
        ) : null}
        <Section variant='card' aria-labelledby='year-heading'>
          <YearSummary stats={stats} alerts={alertList} />
        </Section>
      </div>
    </>
  );
};

export default Dashboard;
